import { Head } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"

// Every front-end page in one list, one click each.
//
// The site has live pages that are deliberately unlisted — noindex, out of the
// sitemap, out of the nav, sent by hand. /pricing, /work, /ai and
// /podcast-scorecard are all real and all previously invisible from inside the
// app, so the only way to find one was to remember it existed. Unlisted is
// listed first here because it is the group that gets lost.

type Page = { path: string; name: string; blurb: string; openable: boolean }
type Group = { key: string; title: string; blurb: string; pages: Page[] }

export default function AdminPages({ groups }: { groups: Group[] }) {
  return (
    <>
      <Head title="Pages">
        <meta name="description" content="Every front-end page on the FullCast site." />
        <meta property="og:title" content="Pages" />
        <meta property="og:description" content="Every front-end page on the FullCast site." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Pages</h1>
          <p className="mt-1">
            Every page that has been built, whether or not anything links to it.
          </p>
        </div>

        {groups.map((group) => (
          <section key={group.key} className="mt-10">
            <h2>{group.title}</h2>
            <p className="mt-1 max-w-2xl text-ink-muted">{group.blurb}</p>

            <ul className="mt-5 divide-y divide-hairline border-y border-hairline">
              {group.pages.map((page) => (
                <li key={page.path} className="py-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    {page.openable ? (
                      <a
                        href={page.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-medium no-underline hover:underline"
                      >
                        {page.name}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="font-medium text-ink-display">{page.name}</span>
                    )}
                    <code className="text-sm text-ink-muted">{page.path}</code>
                  </div>
                  {page.blurb && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{page.blurb}</p>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </AdminShell>
    </>
  )
}
